import { addedDiff, deletedDiff, updatedDiff } from "deep-object-diff"
import PropTypes from "prop-types"
import { createContext, useContext, useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"
import {
  fetchCategory,
  listArticles,
  updateCategory,
} from "~/actions/categories"
import { listCourses } from "~/actions/courses"
import { listPaths } from "~/actions/paths"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"
import serialize from "~/store/serialize"
import differenceBetween from "~/utils/differenceBetween"
import equalAndSameOrder from "~/utils/equalAndSameOrder"

import { useHistory } from "react-router-dom"
import AppContext from "./App"

const CategoryContext = createContext()
export default CategoryContext

export const CategoryContainer = ({ children, categoryId }) => {
  const history = useHistory()
  const queryClient = useQueryClient()
  const { openSnackBar } = useContext(AppContext)
  const openErrorSnackbar = useOpenErrorSnackbar()

  const { data: courses, isLoading: isLoadingCourses } = useQuery(
    "courses",
    () => listCourses(),
  )
  const { data: learningPaths, isLoading: isLoadingLearningPaths } = useQuery(
    "paths",
    listPaths,
  )
  const { data: articles, isLoading: isLoadingArticles } = useQuery(
    "articles",
    listArticles,
  )

  const {
    data: category,
    refetch: refetchCategory,
    isLoading: isLoadingCategory,
  } = useQuery(
    ["category", categoryId],
    () => {
      return fetchCategory(categoryId)
    },
    {
      enabled: Boolean(categoryId),
    },
  )
  const [unsavedState, setUnsavedState] = useState([])
  const [anchorEl, setAnchorEl] = useState(null)
  const [categoryCourses, setCategoryCourses] = useState([])
  const [categoryLearningPaths, setCategoryPaths] = useState([])
  const [initialCategoryCourses, setInitialCategoryCourses] = useState([])
  const [initialCategoryPaths, setInitialCategoryPaths] = useState([])
  const [saveLoading, setSaveLoading] = useState(false)
  const [categoryArticles, setCategoryArticles] = useState([])
  const [initialCategoryArticles, setInitialCategoryArticles] = useState([])
  const [articleDetailValues, setArticleDetailValues] = useState({})
  const [selectedArticle, setSelectedArticle] = useState({})

  //once try to save, it becomes true entirety until refresh - error message handling
  const [tryToSave, setTryToSave] = useState(false)

  const categoryLoading = useMemo(() => {
    return isLoadingCategory || isLoadingCourses || isLoadingLearningPaths
  }, [isLoadingCategory, isLoadingCourses, isLoadingLearningPaths])
  //close menu
  const handleClose = () => {
    setAnchorEl(null)
  }

  const [debouncedDetails, setDebouncedDetails] = useState({
    name: "",
    description: "",
  })

  //certification general information state
  const [catDetailsValues, setCatDetailsValues] = useState({})

  const [initialDetailsContext, setInitialDetailsContext] = useState({})

  const updateCategoryMutation = useMutation((data) =>
    updateCategory(category.id, data),
  )

  useEffect(() => {
    if (learningPaths && learningPaths.length > 0 && category?.learning_paths) {
      const paths = category.learning_paths.map(({ learning_path_id }) =>
        learningPaths.find((lp) => lp.id === learning_path_id),
      )
      setCategoryPaths(paths)
      setInitialCategoryPaths(paths)
    }
  }, [category, learningPaths])

  useEffect(() => {
    // Get all courses and sort by version or return grouped by container id course from api
    if (courses && courses.length > 0 && category?.courses) {
      const sortedCourses = courses.sort(
        (a, b) => new Date(b.publish_date) - new Date(a.publish_date),
      )
      const categoryCourses = category.courses
        .map(({ container_id }) => {
          return sortedCourses.find((c) => c.container_id == container_id)
        })
        .filter((c) => c)
      setCategoryCourses(categoryCourses)
      setInitialCategoryCourses(categoryCourses)
    }
  }, [category, courses])

  useEffect(() => {
    if (articles && articles.length > 0 && category?.articles) {
      const arts = category.articles.map(({ article_id }) =>
        articles.find((art) => art.id === article_id),
      )
      setCategoryArticles(arts)
      setInitialCategoryArticles(arts)
    }
  }, [category, articles])

  //check unsaved details data
  useEffect(() => {
    const unsavedData = updatedDiff(initialDetailsContext, catDetailsValues)
    const addedData = addedDiff(initialDetailsContext, catDetailsValues)

    const deletedData = deletedDiff(initialDetailsContext, catDetailsValues)
    if (
      Object.keys(unsavedData)?.length > 0 &&
      !unsavedState.includes("details")
    ) {
      setUnsavedState((oldState) => [...oldState, "details"])
    } else if (
      Object.keys(unsavedData)?.length === 0 &&
      unsavedState.includes("details")
    ) {
      setUnsavedState(
        unsavedState.filter((x) => {
          return x !== "details"
        }),
      )
    }
    //new, added diff reads for added values to an array
    if (
      Object.keys(addedData)?.length > 0 &&
      !unsavedState.includes("channels")
    ) {
      setUnsavedState((oldState) => [...oldState, "channels"])
    } else if (
      Object.keys(addedData)?.length === 0 &&
      unsavedState.includes("channels")
    ) {
      setUnsavedState(
        unsavedState.filter((x) => {
          return x !== "channels"
        }),
      )
    }
    //new, deleted diff reads for values removed from nested array

    if (
      Object.keys(deletedData)?.length > 0 &&
      !unsavedState.includes("icons")
    ) {
      setUnsavedState((oldState) => [...oldState, "icons"])
    } else if (
      Object.keys(deletedData)?.length === 0 &&
      unsavedState.includes("icons")
    ) {
      setUnsavedState(
        unsavedState.filter((x) => {
          return x !== "icons"
        }),
      )
    }
  }, [catDetailsValues, JSON.stringify(category)])

  //check unsaved courses data
  useEffect(() => {
    let initialValues = initialCategoryCourses.map((x) =>
      Number(x.container_id),
    )
    let currentValues = categoryCourses.map((x) => Number(x.container_id))

    //check the order
    let unsavedData = equalAndSameOrder(initialValues, currentValues)
    //check if are the same
    let unsavedData2 = differenceBetween(initialValues, currentValues)
    if (
      (!unsavedData || unsavedData2?.length > 0) &&
      !unsavedState.includes("courses")
    ) {
      setUnsavedState((oldState) => [...oldState, "courses"])
    } else if (
      unsavedData &&
      unsavedData2?.length === 0 &&
      unsavedState.includes("courses")
    ) {
      setUnsavedState((oldState) =>
        oldState.filter((x) => {
          return x !== "courses"
        }),
      )
    }
  }, [categoryCourses, JSON.stringify(category)])

  //check unsaved articles data
  useEffect(() => {
    let initialValues = [...initialCategoryArticles]
    let currentValues = [...categoryArticles]

    //check the order
    let unsavedData = equalAndSameOrder(initialValues, currentValues)
    //check if are the same
    let unsavedData2 = differenceBetween(initialValues, currentValues)

    if (
      (!unsavedData || unsavedData2?.length > 0) &&
      !unsavedState.includes("articles")
    ) {
      setUnsavedState((oldState) => [...oldState, "articles"])
    } else if (
      unsavedData &&
      unsavedData2?.length === 0 &&
      unsavedState.includes("articles")
    ) {
      setUnsavedState((oldState) =>
        oldState.filter((x) => {
          return x !== "articles"
        }),
      )
    }
  }, [categoryArticles, JSON.stringify(category)])

  //check unsaved paths data
  useEffect(() => {
    let initialValues = initialCategoryPaths.map((x) => Number(x.id))
    let currentValues = categoryLearningPaths.map((x) => Number(x.id))

    //check the order
    let unsavedData = equalAndSameOrder(initialValues, currentValues)
    //check if are the same
    let unsavedData2 = differenceBetween(initialValues, currentValues)
    if (
      (!unsavedData || unsavedData2?.length > 0) &&
      !unsavedState.includes("paths")
    ) {
      setUnsavedState((oldState) => [...oldState, "paths"])
    } else if (
      unsavedData &&
      unsavedData2?.length === 0 &&
      unsavedState.includes("paths")
    ) {
      setUnsavedState((oldState) =>
        oldState.filter((x) => {
          return x !== "paths"
        }),
      )
    }
  }, [categoryLearningPaths, JSON.stringify(category)])

  const updateDetails = () => {
    if (history?.location?.pathname?.includes("seo")) {
      const saveSeo = document.getElementById("save-seo")
      saveSeo?.click()
    } else {
      return new Promise((resolve, reject) => {
        setTryToSave(true)
        if (
          catDetailsValues.type === "partner" &&
          catDetailsValues.partner_status !== "internal" &&
          (catDetailsValues.partner_status === null ||
            catDetailsValues.link === null ||
            catDetailsValues.link.length === 0)
        ) {
          catDetailsValues.partner_status === null
            ? openSnackBar({ message: "Partner Status Required" })
            : openSnackBar({ message: "Partner Link Required" })
          reject()
          return
        }

        if (
          debouncedDetails.name === "" ||
          debouncedDetails?.name?.length > 50
        ) {
          reject()
          return
        }

        if (
          unsavedState.includes("details") ||
          unsavedState.includes("paths") ||
          unsavedState.includes("courses") ||
          unsavedState.includes("channels") ||
          unsavedState.includes("articles") ||
          unsavedState.includes("icons")
        ) {
          setSaveLoading(true)
          const orderedCourses = categoryCourses.map((c, i) => ({
            course_id: c.id,
            course_container_id: c.container_id,
            order: i,
          }))
          const orderedLearningPaths = categoryLearningPaths.map((lp, i) => ({
            learning_path_id: lp.id,
            order: i,
          }))
          const orderedDocs = categoryArticles
            .filter((x) => x?.type === "doc")
            .map((y, i) => {
              return {
                article_id: y?.id,
                order: i,
              }
            })
          const orderedUpdates = categoryArticles
            .filter((x) => x?.type === "update")
            .map((y, i) => {
              return {
                article_id: y?.id,
                order: i,
              }
            })

          const orderedArticles = orderedDocs.concat(orderedUpdates)

          const updateData = {
            ...debouncedDetails,
            courses: orderedCourses,
            learning_paths: orderedLearningPaths,
            articles: orderedArticles,
          }

          updateCategoryMutation.mutate(updateData, {
            onSuccess: (res) => {
              serialize("category", res.data).then((serializedData) => {
                queryClient.setQueryData(
                  ["category", res.data.id + ""],
                  (oldData) => {
                    oldData = serializedData
                    return oldData
                  },
                )
              })
              serialize("category", res.data).then((serializedData) => {
                return queryClient.setQueryData("categories", (oldData) => {
                  let newArray = []
                  newArray = oldData.map((x) => {
                    if (x.id !== serializedData.id) {
                      return x
                    } else {
                      return serializedData
                    }
                  })
                  return [...newArray]
                })
              })
              refetchCategory()
              setSaveLoading(false)
              resolve()
              openSnackBar({
                message: "Category updated successfully",
              })
            },
            onError: (err) => {
              setSaveLoading(false)

              openErrorSnackbar(err, "Error Updating Category")
              reject()
            },
          })
        } else {
          resolve()
        }
      })
    }
  }

  //discard changes
  const discardChanges = () => {
    if (unsavedState.includes("details")) {
      setCatDetailsValues(initialDetailsContext)
      setDebouncedDetails(initialDetailsContext)
    } else if (unsavedState.includes("courses")) {
      setCategoryCourses(category?.courses)
    } else if (unsavedState.includes("paths")) {
      setCategoryPaths(category?.learning_paths)
    } else if (unsavedState.includes("articles")) {
      setCategoryArticles(category?.articles)
    }
  }

  return (
    <CategoryContext.Provider
      value={{
        category,
        initialDetailsContext,
        setInitialDetailsContext,
        debouncedDetails,
        setDebouncedDetails,
        catDetailsValues,
        setCatDetailsValues,
        updateCategoryMutation,
        updateDetails,
        anchorEl,
        setAnchorEl,
        handleClose,
        categoryCourses,
        setCategoryCourses,
        learningPaths,
        categoryLearningPaths,
        setCategoryPaths,
        tryToSave,
        unsavedState,
        saveLoading,
        discardChanges,
        categoryLoading,
        refetchCategory,
        isLoadingLearningPaths,
        isLoadingCourses,
        categoryArticles,
        setCategoryArticles,
        articleDetailValues,
        setArticleDetailValues,
        selectedArticle,
        setSelectedArticle,
      }}
    >
      {children}
    </CategoryContext.Provider>
  )
}

CategoryContainer.propTypes = {
  children: PropTypes.node,
  categoryId: PropTypes.number,
}
